import { en } from '@/locales/en'
import { lt } from '@/locales/lt'

export type Locale = 'en' | 'lt'
export type MessageKey = keyof typeof en
/** Every language must define every key (TypeScript enforces this), except extra plural forms. */
export type Messages = Record<MessageKey, string> & Partial<Record<PluralKey, string>>

type PluralBase = MessageKey extends infer K ? (K extends `${infer Base}.other` ? Base : never) : never
type PluralKey = `${PluralBase}.${Intl.LDMLPluralRule}`
type Vars = Record<string, string | number>

const MESSAGES: Record<Locale, Messages> = { en, lt }

// The current locale comes from the server (shared Inertia prop) and is set by the persistent Layout
// before any page renders, so plain functions (toasts, date helpers) can translate too.
let current: Locale = 'en'

export function setLocale(locale: Locale) {
  current = MESSAGES[locale] ? locale : 'en'
}

export const getLocale = () => current

function interpolate(template: string, vars?: Vars) {
  return vars ? template.replace(/\{(\w+)\}/g, (match, name) => (name in vars ? String(vars[name]) : match)) : template
}

export function t(key: MessageKey, vars?: Vars) {
  return interpolate(MESSAGES[current][key] ?? en[key], vars)
}

/** Plural-aware lookup: picks `${base}.one|few|many|other` using the language's plural rules. */
export function tp(base: PluralBase, count: number, vars?: Vars) {
  const form = new Intl.PluralRules(current).select(count)
  const messages = MESSAGES[current] as Record<string, string | undefined>
  const template = messages[`${base}.${form}`] ?? messages[`${base}.other`] ?? en[`${base}.other` as MessageKey]
  return interpolate(template, { count, ...vars })
}
